import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  forwardRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

import { LoggerService } from '@services/logger.service';
import { AppState } from 'src/app/reducers';
import { searchPosts } from 'src/app/core/actions/feed.actions';

import { debounceTime, distinctUntilChanged, tap } from 'rxjs';

@Component({
  selector: 'app-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SearchInputComponent implements ControlValueAccessor {
  /**
   * Display text (already translated) for the input placeholder. Call sites pass
   * a translated string - e.g. `[placeholder]="'home-actions.placeholder.search' | translate"`.
   * Left unset, it falls back to the translated generic `search-input.input.search`.
   */
  @Input() placeholder?: string;
  @Input() value: string = '';
  @Input() debounceTime: number = 300;
  @Input() isDisabled: boolean = false;
  @Input() isAdvancedSearch: boolean = false;
  @Output() inputChanged = new EventEmitter<string>(); // Emit events when the input value changes
  /**
   * Explicit submit — fires on Enter (with the live, un-debounced value) and on
   * clear (empty string). Feed-agnostic: use this for type-and-Enter search
   * (e.g. the ops grids) instead of reacting to every debounced `inputChanged`.
   */
  @Output() search = new EventEmitter<string>();
  @Output() historyOpened = new EventEmitter<boolean>();
  deleteIconVisible: boolean = false;

  public searchControl: FormControl = new FormControl();
  public faSearch = faSearch; // Icon to be used in the template

  private onTouched: Function = () => {};
  private onChange: Function = () => {};

  protected destroyRef = inject(DestroyRef);
  protected loggerService = inject(LoggerService);

  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        tap((value: string | null | undefined) => {
          // propagate value
          this.inputChanged.emit(value);
          this.onChange(value);

          // show delete icon only when there's something to clear
          this.deleteIconVisible = !!value;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.searchControl.setValue(this.value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value']) {
      this.searchControl.setValue(changes['value']?.currentValue);
    }
    if (changes['isDisabled']) {
      changes['isDisabled'].currentValue
        ? this.searchControl.disable()
        : this.searchControl.enable();
    }
  }

  writeValue(value: any): void {
    if (value !== null && value !== undefined) {
      this.searchControl.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  protected markTouched(): void {
    this.onTouched();
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  resetFilter() {
    const value = '';
    this.searchControl.setValue(value, { emitEvent: true });
    this.inputChanged.emit(value);
    this.search.emit(value);
    if (this.isAdvancedSearch) {
      this.store.dispatch(searchPosts({ query: value }));
    }
  }

  /** Enter key — submit the current (live) value. */
  onEnter() {
    this.search.emit(this.searchControl.value ?? '');
  }

  openHistrory() {
    this.historyOpened.emit(true);
  }
}
